import React from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { PersonaCardProps } from './types';
import { logger } from '../../utils/logger';

const PersonaCardContainer = styled.div<{ selected: boolean; disabled?: boolean }>`
  background: ${props => props.selected ? theme.colors.gradients.primary : theme.colors.background.card};
  border: 2px solid ${props => props.selected ? theme.colors.primary : theme.colors.border.light};
  border-radius: ${theme.borderRadius.lg};
  padding: 2rem;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all ${theme.transitions.normal};
  opacity: ${props => props.disabled ? 0.6 : 1};
  transform: ${props => props.selected ? 'translateY(-4px)' : 'translateY(0)'};
  box-shadow: ${props => props.selected 
    ? theme.shadows.heavy
    : theme.shadows.light};
  
  &:hover {
    transform: ${props => props.disabled ? 'translateY(0)' : 'translateY(-2px)'};
    box-shadow: ${props => props.disabled 
      ? theme.shadows.light
      : theme.shadows.medium};
  }
`;

const PersonaIcon = styled.div`
  font-size: 3rem;
  text-align: center;
  margin-bottom: 1rem;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
`;

const PersonaName = styled.h3<{ selected: boolean }>`
  color: ${props => props.selected ? theme.colors.text.light : theme.colors.text.primary};
  font-size: 1.5rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 0.5rem;
`;

const PersonaTagline = styled.p<{ selected: boolean }>`
  color: ${props => props.selected ? 'rgba(255, 255, 255, 0.9)' : theme.colors.text.secondary};
  font-size: 0.9rem;
  text-align: center;
  margin-bottom: 1rem;
  line-height: 1.4;
`;

const PersonaTraits = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
  pointer-events: none;
`;

const TraitTag = styled.span<{ selected: boolean }>`
  background: ${props => props.selected 
    ? 'rgba(255, 255, 255, 0.2)' 
    : theme.colors.background.secondary};
  color: ${props => props.selected ? theme.colors.text.light : theme.colors.text.primary};
  padding: 0.25rem 0.75rem;
  border-radius: ${theme.borderRadius.full};
  font-size: 0.75rem;
  font-weight: 500;
  border: 1px solid ${props => props.selected 
    ? 'rgba(255, 255, 255, 0.3)' 
    : theme.colors.border.light};
`;

const SelectionIndicator = styled.div<{ selected: boolean }>`
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${props => props.selected ? theme.colors.text.light : 'transparent'};
  border: 2px solid ${props => props.selected ? theme.colors.primary : theme.colors.border.medium};
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  
  &::after {
    content: '✓';
    color: ${theme.colors.primary};
    font-size: 14px;
    font-weight: bold;
    opacity: ${props => props.selected ? 1 : 0};
    transition: opacity 0.2s ease;
  }
`;

const CardWrapper = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  cursor: pointer;
`;

export const PersonaCard: React.FC<PersonaCardProps> = ({
  persona,
  onSelect,
  selected,
  disabled = false
}) => {
  const handleClick = () => {
    logger.log('PersonaCard clicked:', persona.id, 'disabled:', disabled);
    if (!disabled) {
      onSelect(persona.id);
    }
  };

  return (
    <CardWrapper onClick={handleClick}>
      <PersonaCardContainer
        selected={selected}
        disabled={disabled}
        
      >
        <SelectionIndicator selected={selected} />
        
        <PersonaIcon>
          {persona.visualIdentity.imagery[0]}
        </PersonaIcon>
        
        <PersonaName selected={selected}>
          {persona.name}
        </PersonaName>
        
        <PersonaTagline selected={selected}>
          {persona.tagline}
        </PersonaTagline>
        
        <PersonaTraits>
          {persona.traits.slice(0, 3).map(trait => (
            <TraitTag key={trait} selected={selected}>
              {trait}
            </TraitTag>
          ))}
        </PersonaTraits>
      </PersonaCardContainer>
    </CardWrapper>
  );
};
